/**
 * PendingApprovalsCard (HR Delegation Phase 3) — owner เห็น "คำขอรออนุมัติจาก HR"
 * (action ที่ HR ระดับ tier=pending ส่งมา เช่น ขอปิดงวด). owner กด "ไปจัดการ" เพื่อทำ
 * action เองผ่าน flow ปกติ (คำขอ auto-resolve เมื่อทำสำเร็จ) หรือ "ปฏิเสธ".
 * แสดงเฉพาะ owner + เมื่อมีคำขอค้าง (ไม่ noise).
 */
import { Link } from 'react-router-dom';
import { ShieldQuestion, ChevronRight } from 'lucide-react';
import { usePendingActions, useRejectPendingAction, type PendingAction } from '../hooks';
import { useToast } from './ui/toast';

/** action_type → หน้าที่ owner ไปทำ action นั้น */
function actionRoute(a: PendingAction): string {
  switch (a.action_type) {
    case 'payroll_lock': return '/owner/cycle';
    case 'salary_edit': return '/hr/people';
    case 'invite_over_level': return '/launcher';
    default: return '/owner/cycle';
  }
}

export function PendingApprovalsCard() {
  const { data: items } = usePendingActions();
  const reject = useRejectPendingAction();
  const toast = useToast();
  if (!items || items.length === 0) return null; // hide when empty

  return (
    <section className="card p-4 space-y-3 ring-1 ring-amber-200">
      <div className="flex items-center gap-2">
        <ShieldQuestion className="w-5 h-5 text-amber-500" />
        <h2 className="font-display font-semibold">คำขอรออนุมัติจาก HR</h2>
        <span className="ml-auto badge bg-amber-50 text-amber-700">{items.length}</span>
      </div>
      <div className="space-y-2">
        {items.map((a) => (
          <div key={a.id} className="border-b border-gray-50 last:border-0 pb-2 last:pb-0">
            <p className="text-sm font-medium text-gray-900">{a.summary}</p>
            <p className="text-xs text-gray-400">
              โดย {a.requested_by_name ?? '—'} · {new Date(a.created_at).toLocaleString('th-TH', { dateStyle: 'short', timeStyle: 'short' })}
            </p>
            <div className="flex items-center gap-3 mt-1.5">
              <Link to={actionRoute(a)} className="text-sm font-medium text-teal inline-flex items-center gap-1 hover:underline">
                ตรวจ & ทำให้ <ChevronRight className="w-3.5 h-3.5" />
              </Link>
              <button
                type="button"
                disabled={reject.isPending}
                onClick={() => reject.mutate({ id: a.id }, {
                  onSuccess: () => toast.info('ปฏิเสธคำขอแล้ว'),
                  onError: () => toast.error('ปฏิเสธไม่สำเร็จ'),
                })}
                className="text-sm text-gray-400 hover:text-red-600"
              >
                ปฏิเสธ
              </button>
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-400">เจ้าของเป็นผู้ทำรายการนี้เอง — กด “ตรวจ & ทำให้” เพื่อไปยืนยัน · คำขอจะปิดอัตโนมัติเมื่อทำสำเร็จ</p>
    </section>
  );
}
